<ng-container *ngIf="primaryFacility">
  <div class="facility-header-wrapper" [class.is-collapsed]="isCollapsed" data-testid="fac-header-wrapper">
    <!-- Main Info Card -->
    <div class="facility-card" data-testid="fac-header-card">
      <div class="facility-content">
        <!-- Top row: identity on the left, company credit tiles on the right -->
        <div class="header-top">
          <div class="facility-identity">
            <div class="header-name-row">
              <dx-button [icon]="isCollapsed ? 'chevrondown' : 'chevronup'" class="collapse-toggle-btn"
                hint="{{ isCollapsed ? 'Show Details' : 'Hide Details' }}" (onClick)="toggleCollapse()"
                [elementAttr]="{ 'data-testid': 'fac-header-btn-toggle-collapse' }">
              </dx-button>
              <div class="name-container">
                <div class="label-ordering">Ordering for:</div>
                <h2 class="facility-name">{{ primaryFacility.name }}</h2>
                <div class="company-name" *ngIf="company?.name">Company: {{ company.name }}</div>
              </div>
            </div>
          </div>

          <div class="stat-tiles" data-testid="fac-header-company-info">
            <div class="stat-tile">
              <div class="stat-label">Payment Type</div>
              <div class="stat-value" *ngIf="paymentTypeLabel; else missingData">{{ paymentTypeLabel }}</div>
            </div>
            <ng-container *ngIf="credit">
              <div class="stat-tile" [class.stat-tile-over]="credit.available_credit < 0">
                <div class="stat-label">Available Credit</div>
                <div class="stat-value" [class.text-over-limit]="credit.available_credit < 0"
                  data-testid="fac-header-available-credit">{{ credit.available_credit | currency }}</div>
              </div>
              <div class="stat-tile">
                <div class="stat-label">Credit Limit</div>
                <div class="stat-value" data-testid="fac-header-credit-limit">{{ credit.credit_limit | currency }}</div>
              </div>
            </ng-container>
          </div>
        </div>

        <!-- Licence alert: full-width band, shown even when collapsed -->
        <div *ngIf="expiredLicense || expiredMessage" class="expired-alert">
          <div class="alert-icon">
            <i class="dx-icon-warning"></i>
          </div>
          <div class="alert-content">
            <div class="alert-title">{{ expiredTitle }}</div>
            <div class="alert-message">
              {{ expiredMessage }} <a [routerLink]="['/pharmacies']" [queryParams]="{ license: licenseToFix }" class="alert-link">Update the license</a>
            </div>
          </div>
        </div>

        <!-- Details: Facility | Contact | Shipping -->
        <div class="facility-details-row" *ngIf="!isCollapsed">
          <div>
            <div class="section-label">Facility</div>
            <div class="licenses-container">
              <div *ngIf="primaryFacility.dea_number && featureFlagsService.getByEnv('ui','DEA')" class="license-item">
                <span class="license-label">DEA</span>
                <span class="license-value" [class.license-expired]="licenseStatus?.dea_license_expired">{{ primaryFacility.dea_number }}
                  <span *ngIf="licenseStatus?.dea_license_expired" class="expired-pill">Expired</span></span>
              </div>
              <div *ngIf="primaryFacility.state_license" class="license-item">
                <span class="license-label">State License</span>
                <span class="license-value" [class.license-expired]="licenseStatus?.state_license_expired">{{ primaryFacility.state_license }}
                  <span *ngIf="licenseStatus?.state_license_expired" class="expired-pill">Expired</span></span>
              </div>
              <div class="license-item">
                <span class="license-label">Portal Account ID</span>
                <span class="license-value">{{ primaryFacility.company_id }}-{{primaryFacility.id}}</span>
              </div>
              <div *ngIf="company?.dwolla_customer_id && primaryFacility.vendor_number" class="license-item">
                <span class="license-label">Caretria Account ID</span>
                <span class="license-value">{{company?.dwolla_customer_id}}-{{ primaryFacility.vendor_number }}</span>
              </div>
            </div>
          </div>

          <div>
            <div class="section-label">Contact</div>
            <div class="licenses-container">
              <div class="license-item">
                <span class="license-label">Phone</span>
                <span class="license-value" *ngIf="primaryFacility.phone; else missingData">{{ primaryFacility.phone }}</span>
              </div>
              <div class="license-item">
                <span class="license-label">Email</span>
                <span class="license-value" *ngIf="primaryFacility.email; else missingData">{{ primaryFacility.email }}</span>
              </div>
            </div>
          </div>

          <div class="facility-shipping">
            <div class="shipping-label-row">
              <div class="section-label mb-0">Shipping To</div>
              <dx-button *ngIf="canEditFacility" icon="edit" class="edit-address-btn" stylingMode="text" (onClick)="openEditFacilityModal()" hint="Edit Facility"></dx-button>
            </div>
            <address class="shipping-address">
              {{ primaryFacility.delivery_address.line_1 | titlecase }}<br />
              <ng-container *ngIf="primaryFacility.delivery_address.line_2">
                {{ primaryFacility.delivery_address.line_2 | titlecase }}<br />
              </ng-container>
              <ng-container *ngIf="primaryFacility.delivery_address.line_3">
                {{ primaryFacility.delivery_address.line_3 | titlecase }}<br />
              </ng-container>
              {{ primaryFacility.delivery_address.city | titlecase }},
              {{ primaryFacility.delivery_address.state }},
              {{ primaryFacility.delivery_address.postal_code }}
            </address>
          </div>
        </div>
        <ng-template #missingData><span class="license-value text-muted font-italic">missing</span></ng-template>

        <!-- Actions -->
        <div *ngIf="!isCollapsed && !isSmall" class="facility-actions-row">
          <div class="actions-container">
            <dx-button type="default" stylingMode="outlined" text="Place Order" class="action-btn"
              (onClick)="router.navigate(['/catalog'])"
              [elementAttr]="{ 'data-testid': 'fac-header-btn-place-order' }"></dx-button>
            <dx-button type="default" stylingMode="outlined" text="Order Details" class="action-btn"
              (onClick)="router.navigate(['/orders'])"
              [elementAttr]="{ 'data-testid': 'fac-header-btn-order-details' }"></dx-button>
            <dx-button type="default" stylingMode="outlined" text="Facility Info" class="action-btn"
              (onClick)="router.navigate(['/pharmacies'])"
              [elementAttr]="{ 'data-testid': 'fac-header-btn-facility-info' }"></dx-button>
            <dx-button *ngIf="showBulkPayment" type="default" stylingMode="outlined" text="Bulk Payment"
              class="action-btn" (onClick)="lumpVisible = true"
              [elementAttr]="{ 'data-testid': 'fac-header-btn-bulk-payment' }"></dx-button>
          </div>
        </div>
      </div>

      <!-- Vetted Warning Banner -->
      <div *ngIf="!isCollapsed && !primaryFacility.vetted" class="vetted-warning">
        <div class="warning-content">
          You have not yet been approved as a buyer, so you cannot see direct pricing yet.
        </div>
      </div>

      <!-- Past Due Warning Banner -->
      <div
        *ngIf="!isCollapsed && pastDueCount > 0"
        class="vetted-warning past-due-warning"
        style="cursor: pointer;"
        (click)="pastDue.routeToPastDue(pastDueSummary, primaryFacility.id)"
        data-testid="fac-header-past-due-warning"
      >
        <div class="warning-content">
          {{ pastDueCount }} orders past due &middot; {{ pastDueAmount | currency }} outstanding
        </div>
      </div>
    </div>
  </div>
</ng-container>

<app-lump-payment-links
  *ngIf="showBulkPayment"
  [companyId]="primaryFacility?.company_id"
  [(visible)]="lumpVisible"
></app-lump-payment-links>

<app-edit-facility-modal
  *ngIf="showEditFacilityModal"
  [(visible)]="showEditFacilityModal"
  [facilityId]="editFacilityId"
  [formData]="editFacilityFormData"
  (onUpdate)="getProfileInformation()"
></app-edit-facility-modal>
